import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import { content } from "../../content/content";
import { fadeUp, staggerContainer } from "../../hooks/useScrollReveal";
import { SunRays } from "../Ornaments";

interface CohortDatum {
  id: string;
  label: string;
  stitching: number;
  computer: number;
  status: "complete" | "in-progress";
}

const COLORS = {
  stitching: "#a8521a",
  computer: "#8a3856",
};

const STUDENTS_PER_STITCH = 10;
const STITCH_WIDTH = 18;
const STITCH_HEIGHT = 8;
const STITCH_GAP = 6;
const PROGRAM_GAP = 20;

function HeroNumber() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [count, setCount] = useState(0);
  const target = content.impact.heroNumber;

  useEffect(() => {
    if (!isInView) {
      return;
    }
    let start = 0;
    const duration = 1800;
    const stepTime = Math.max(Math.floor(duration / target), 16);
    const timer = setInterval(() => {
      start += Math.ceil(target / (duration / stepTime));
      if (start >= target) {
        start = target;
        clearInterval(timer);
      }
      setCount(start);
    }, stepTime);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return (
    <div ref={ref} className="relative flex flex-col items-start">
      <p className="font-display text-[clamp(4.5rem,11vw,9rem)] leading-[0.88] font-light tracking-[-0.03em] text-ink">
        <span data-edit-id="content.impact.heroNumber">{count}</span>
        <span data-edit-id="content.impact.heroSuffix" className="text-saffron">
          {content.impact.heroSuffix}
        </span>
      </p>
      <p
        data-edit-id="content.impact.heroLabel"
        className="mt-5 font-body text-sm tracking-[0.2em] text-ink-muted uppercase"
      >
        {content.impact.heroLabel}
      </p>
      <p
        data-edit-id="content.impact.heroSubLabel"
        className="mt-2 max-w-[18ch] font-body text-sm text-ink-faint italic"
      >
        {content.impact.heroSubLabel}
      </p>
    </div>
  );
}

/**
 * One running-stitch mark — a short horizontal stroke with rounded ends,
 * the way a sewing student would lay down stitches across a piece of cloth.
 * A small per-mark tilt keeps the row from feeling mechanical.
 */
function StitchMark({
  color,
  inProgress,
  jitter,
}: {
  color: string;
  inProgress: boolean;
  jitter: number;
}) {
  const mid = STITCH_HEIGHT / 2;
  const inset = 1.5;
  const stroke = inProgress ? 2.0 : 2.4;
  const dasharray = inProgress ? "3 2" : undefined;
  return (
    <svg
      width={STITCH_WIDTH}
      height={STITCH_HEIGHT}
      viewBox={`0 0 ${STITCH_WIDTH} ${STITCH_HEIGHT}`}
      style={{
        flex: "none",
        transform: `rotate(${jitter * 4}deg)`,
      }}
    >
      <line
        x1={inset}
        y1={mid + jitter}
        x2={STITCH_WIDTH - inset}
        y2={mid - jitter}
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={dasharray}
      />
    </svg>
  );
}

function CrossStitchRow({ cohort }: { cohort: CohortDatum }) {
  const inProgress = cohort.status === "in-progress";
  const stitchCount = Math.max(
    1,
    Math.round(cohort.stitching / STUDENTS_PER_STITCH),
  );
  const computerCount = Math.max(
    1,
    Math.round(cohort.computer / STUDENTS_PER_STITCH),
  );

  // Stable, gentle per-stitch jitter so rows aren't mechanically uniform.
  // Deterministic from cohort.id + index so SSR / hydration agree.
  const jitterFor = (key: string, i: number) => {
    let h = 0;
    const seed = `${key}-${i}`;
    for (let n = 0; n < seed.length; n++) {
      h = (h * 31 + seed.charCodeAt(n)) | 0;
    }
    return ((Math.abs(h) % 11) - 5) / 18; // [-0.27 ... 0.27]
  };

  return (
    <motion.figure
      data-edit-id={`content.impact.cohorts[${cohort.id}]`}
      className="grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-center gap-x-5 md:gap-x-7"
      variants={fadeUp}
    >
      <p
        data-edit-id={`content.impact.cohorts[${cohort.id}].label`}
        className="font-body text-[11px] tracking-[0.22em] text-ink-light uppercase tabular-nums"
      >
        {cohort.label}
      </p>

      <div
        className="flex flex-wrap items-center"
        style={{
          gap: `${STITCH_GAP}px`,
          opacity: inProgress ? 0.62 : 1,
        }}
      >
        {Array.from({ length: stitchCount }).map((_, i) => (
          <StitchMark
            key={`s-${i}`}
            color={COLORS.stitching}
            inProgress={inProgress}
            jitter={jitterFor(`${cohort.id}-s`, i)}
          />
        ))}
        <span
          aria-hidden="true"
          style={{ width: `${PROGRAM_GAP - STITCH_GAP}px` }}
        />
        {Array.from({ length: computerCount }).map((_, i) => (
          <StitchMark
            key={`c-${i}`}
            color={COLORS.computer}
            inProgress={inProgress}
            jitter={jitterFor(`${cohort.id}-c`, i)}
          />
        ))}
      </div>

      <p className="flex items-baseline gap-2 font-body text-xs tracking-[0.14em] text-ink-light tabular-nums uppercase">
        <span className="font-display text-[clamp(1.05rem,1.5vw,1.3rem)] font-light tracking-tight text-ink normal-case">
          <span data-edit-id={`content.impact.cohorts[${cohort.id}].stitching`}>
            {cohort.stitching}
          </span>
          <span className="mx-1 text-ink-faint">+</span>
          <span data-edit-id={`content.impact.cohorts[${cohort.id}].computer`}>
            {cohort.computer}
          </span>
        </span>
        {inProgress && (
          <span className="text-[10px] text-ink-faint italic">
            {content.impact.cohortsInProgressLabel}
          </span>
        )}
      </p>
    </motion.figure>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 font-body text-xs tracking-[0.16em] text-ink-muted uppercase">
      <span className="flex items-center gap-2.5">
        <StitchMark color={COLORS.stitching} inProgress={false} jitter={0} />
        <span data-edit-id="content.impact.cohortsLegendStitching">
          {content.impact.cohortsLegendStitching}
        </span>
      </span>
      <span className="flex items-center gap-2.5">
        <StitchMark color={COLORS.computer} inProgress={false} jitter={0} />
        <span data-edit-id="content.impact.cohortsLegendComputer">
          {content.impact.cohortsLegendComputer}
        </span>
      </span>
      <span className="text-ink-faint normal-case italic tracking-normal">
        one stitch represents ~{STUDENTS_PER_STITCH} students
      </span>
    </div>
  );
}

export function Impact() {
  const cohorts = content.impact.cohorts;

  return (
    <section id="journey" className="paper-grain bg-warm-white py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          className="mb-20 max-w-2xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p
            data-edit-id="content.impact.eyebrow"
            className="mb-4 font-body text-xs font-medium tracking-[0.22em] text-saffron-dark uppercase"
          >
            {content.impact.eyebrow}
          </p>
          <h2
            data-edit-id="content.impact.headline"
            className="text-balance font-display text-[clamp(2rem,4vw,3.5rem)] text-ink"
          >
            {content.impact.headline}
          </h2>
          <p
            data-edit-id="content.impact.intro"
            className="mt-4 font-body text-lg text-ink-muted"
          >
            {content.impact.intro}
          </p>
        </motion.div>

        <motion.div
          className="grid items-start gap-y-16 md:grid-cols-[5fr_7fr] md:gap-x-16 lg:gap-x-24"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <HeroNumber />

          <div className="flex flex-col">
            <div className="mb-7 flex items-baseline justify-between gap-4 border-b border-ink/10 pb-3">
              <p
                data-edit-id="content.impact.cohortsEyebrow"
                className="font-body text-[11px] font-medium tracking-[0.24em] text-saffron-dark uppercase"
              >
                {content.impact.cohortsEyebrow}
              </p>
              <p
                data-edit-id="content.impact.cohortsHeadline"
                className="font-body text-[11px] tracking-[0.18em] text-ink-faint italic"
              >
                {content.impact.cohortsHeadline}
              </p>
            </div>

            <motion.div
              className="grid gap-y-4 md:gap-y-5"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
            >
              {cohorts.map((cohort) => (
                <CrossStitchRow key={cohort.id} cohort={cohort} />
              ))}
            </motion.div>

            <div className="mt-8">
              <Legend />
            </div>
          </div>
        </motion.div>

        <motion.p
          data-edit-id="content.impact.footerLine"
          className="mt-16 text-center font-body text-xs tracking-[0.22em] text-ink-faint uppercase"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {content.impact.footerLine}
        </motion.p>

        <motion.div
          className="mt-20 flex justify-center"
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
        >
          <SunRays className="h-16 w-16 text-saffron-dark/70 md:h-20 md:w-20" />
        </motion.div>

        <motion.div
          className="mx-auto mt-12 max-w-3xl text-center"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h3
            data-edit-id="content.impact.philosophyHeadline"
            className="text-balance font-display text-[clamp(1.4rem,2.8vw,2.2rem)] leading-[1.35] font-light text-ink-light"
          >
            {content.impact.philosophyHeadline}
          </h3>
          <p
            data-edit-id="content.impact.philosophyBody"
            className="mx-auto mt-8 max-w-2xl font-body text-base leading-relaxed text-ink-muted"
          >
            {content.impact.philosophyBody}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
